import { useEffect, useState } from 'react';
import {
  LineInOrder,
  POLineData,
  useDeleteFromActiveOrderMutation,
  useEditOrderMutation,
  useLazyGetCartQuery,
  useLazyGetCartSummaryQuery,
} from '@zonofi/zono-money-store';
import { OrderDataLine } from '../types/interfaces';

interface callCartAPIProps {
  workspaceId: string;
  customerId: string;
  sellerWorkspaceId: string;
  includeActiveOrders?: boolean;
  includeSummary?: boolean;
  importSource?: 'manual' | 'upload' | 'whatsapp';
}

interface SelectedLine extends OrderDataLine, LineInOrder {}

export const useCart = () => {
  const [getCartAPI, cartAPIResponse] = useLazyGetCartQuery();
  const [getCartSummaryAPI, cartSummaryAPIResponse] =
    useLazyGetCartSummaryQuery();
  const [editOrder, editOrderResponse] = useEditOrderMutation();
  const [deleteLineAPI, deleteLinesAPIResponse] =
    useDeleteFromActiveOrderMutation();

  const [productsInCart, setProductsInCart] = useState<POLineData[]>([]);

  const getOrderContext = (selectedLine: SelectedLine) => {
    if (selectedLine?.orderId) {
      return {
        orderId: selectedLine?.orderId?.toString(),
      };
    }
    if (selectedLine?.poFileId) {
      return {
        poFileId: selectedLine?.poFileId,
      };
    }
    return {};
  };

  const callCartAPI = ({
    workspaceId,
    customerId,
    sellerWorkspaceId,
    includeActiveOrders = true,
    includeSummary = false,
    importSource,
  }: callCartAPIProps) => {
    if (workspaceId && customerId && sellerWorkspaceId) {
      getCartAPI({
        workspaceId,
        params: {
          sellerWorkspaceId: sellerWorkspaceId,
          customerId,
          includeActiveOrders: includeActiveOrders,
          includeSummary: includeSummary,
          ...(importSource ? { importSource: importSource } : {}),
        },
      });
      getCartSummaryAPI({
        customerId,
        sellerWorkspaceId,
      });
    }
  };

  const onIncrement = (
    selectedLine: SelectedLine,
    sellerWorkspaceId: string,
    customerId: string
  ) => {
    const context = getOrderContext(selectedLine);
    if (!context?.orderId && !context?.poFileId) {
      return;
    }
    editOrder({
      workspaceId: sellerWorkspaceId,
      body: {
        customerId: customerId,
        lines: [
          {
            quantity: selectedLine.quantity + 1,
            ...(selectedLine?.productVariantId
              ? { productVariantId: selectedLine?.productVariantId }
              : {}),
            ...(selectedLine?.orderLineId
              ? { orderLineId: selectedLine?.orderLineId }
              : {}),
            ...(selectedLine?.poFileLineId
              ? { poFileLineId: selectedLine?.poFileLineId }
              : {}),
            operator: 'add',
          },
        ],
        ...context,
      },
    });
  };

  const onDecrement = (
    selectedLine: SelectedLine,
    sellerWorkspaceId: string,
    customerId: string
  ) => {
    const context = getOrderContext(selectedLine);
    if (!context?.orderId && !context?.poFileId) {
      return;
    }
    editOrder({
      workspaceId: sellerWorkspaceId,
      body: {
        customerId: customerId,
        lines: [
          {
            quantity: selectedLine.quantity - 1,
            ...(selectedLine?.productVariantId
              ? { productVariantId: selectedLine?.productVariantId }
              : {}),
            ...(selectedLine?.orderLineId
              ? { orderLineId: selectedLine?.orderLineId }
              : {}),
            ...(selectedLine?.poFileLineId
              ? { poFileLineId: selectedLine?.poFileLineId }
              : {}),
            operator: 'minus',
          },
        ],
        ...context,
      },
    });
  };

  const onDelete = (
    selectedLine: SelectedLine,
    sellerWorkspaceId: string,
    customerId: string
  ) => {
    if (selectedLine?.orderId) {
      onChangeQuantity(selectedLine, 0, sellerWorkspaceId, customerId);
      return;
    }
    if (selectedLine?.poFileLineId && selectedLine?.poFileId) {
      deleteUnMappedLine(selectedLine, sellerWorkspaceId, customerId);
      return;
    }

    // if (selectedLine?.orderLineId) {
    //   deleteLineAPI({
    //     params: {
    //       sellerWorkspaceId: sellerWorkspaceId,
    //     },
    //     body: {
    //       customerId,
    //       lines: [
    //         {
    //           orderId: selectedLine?.orderId,
    //           orderLineId: selectedLine?.orderLineId,
    //         },
    //       ],
    //       sellerWorkspaceId,
    //     },
    //   });
    // } else {
    //   onChangeQuantity(selectedLine, 0, sellerWorkspaceId, customerId);
    // }
  };

  const deleteUnMappedLine = (
    selectedLine: SelectedLine,
    sellerWorkspaceId: string,
    customerId: string
  ) => {
    if (!selectedLine?.poFileLineId || !selectedLine?.poFileId) {
      return;
    }
    editOrder({
      workspaceId: sellerWorkspaceId,
      body: {
        customerId: customerId,
        lines: [
          {
            poFileLineId: selectedLine?.poFileLineId,
            quantity: 0,
          },
        ],
        poFileId: selectedLine?.poFileId,
      },
    });
  };
  const onChangeQuantity = (
    selectedLine: SelectedLine,
    qty: number,
    sellerWorkspaceId: string,
    customerId: string
  ) => {
    if (qty === selectedLine?.quantity) {
      return;
    }
    const context = getOrderContext(selectedLine);
    if (!context?.orderId && !context?.poFileId) {
      return;
    }
    let operator = 'add';

    if (qty < selectedLine?.quantity) {
      operator = 'minus';
    }
    editOrder({
      workspaceId: sellerWorkspaceId,
      body: {
        customerId: customerId,
        lines: [
          {
            quantity: qty,
            ...(selectedLine?.productVariantId
              ? { productVariantId: selectedLine?.productVariantId }
              : {}),
            ...(selectedLine?.orderLineId
              ? { orderLineId: selectedLine?.orderLineId }
              : {}),
            ...(selectedLine?.poFileLineId
              ? { poFileLineId: selectedLine?.poFileLineId }
              : {}),
            ...(qty !== 0 && { operator: operator }),
          },
        ],
        ...context,
      },
    });
  };

  const onApplyPromotion = ({
    sellerWorkspaceId,
    customerId,
    orderId,
    productVariantId,
    quantity,
    listPrice,
    promotionIds,
  }: {
    sellerWorkspaceId: string;
    customerId: string;
    orderId: string;
    productVariantId: number;
    quantity: number;
    listPrice?: number;
    promotionIds: (string | number)[];
  }) => {
    editOrder({
      workspaceId: sellerWorkspaceId,
      body: {
        customerId,
        orderId,
        lines: [
          {
            quantity,
            productVariantId,
            ...(typeof listPrice === 'number' ? { listPrice } : {}),
          },
        ],
        promotionIds,
      },
    });
  };

  useEffect(() => {
    if (cartAPIResponse?.isSuccess) {
      setProductsInCart(() => {
        let lines: POLineData[] = [];
        cartAPIResponse?.data?.files?.forEach((file) => {
          if (file?.lines) {
            lines.push(
              ...file?.lines?.map((line) => {
                return {
                  ...line,
                  poFileId: file?.id,
                };
              })
            );
          }
        });
        return lines;
      });
    }
  }, [cartAPIResponse]);

  return {
    callCartAPI,
    productsInCart,
    setProductsInCart,
    cartAPIResponse,
    cartSummaryAPIResponse,
    onIncrement,
    onDecrement,
    onDelete,
    onChangeQuantity,
    onApplyPromotion,
    editOrderResponse,
    deleteLinesAPIResponse,
  };
};
